import React, { useState, useEffect } from 'react';
import { Link } from 'react-router';
import { Helmet } from '@dr.pogodin/react-helmet';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { RootState } from 'store';
import LeftNavigationBar from 'screens/Main/components/LeftNavigationBar';
import TopNavigationBar from 'screens/Main/components/TopNavigationBar';
import Footer from 'components/Footer';
import {
  useGetMyApiKeyQuery,
  useRegenerateApiKeyMutation,
  useRevokeApiKeyMutation,
} from 'store/services/partner/partnerApi';
import 'screens/Public/ApiDocumentation/apiDocumentation.css';
import { copyToClipboard } from 'utils/copyToClipboard';
import './partnerApi.css';

// Code block with copy functionality (mirrors the public API docs page)
const CodeBlock: React.FC<{ code: string; label?: string }> = ({
  code,
  label,
}) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (!(await copyToClipboard(code))) return;
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="api-docs-code-block">
      <div className="api-docs-code-header">
        <span className="api-docs-code-label">{label || 'Code'}</span>
        <button
          className={`api-docs-copy-btn ${copied ? 'copied' : ''}`}
          onClick={handleCopy}
        >
          {copied ? 'Copied!' : 'Copy'}
        </button>
      </div>
      <div className="api-docs-code-content">
        <pre>{code}</pre>
      </div>
    </div>
  );
};

// Tabbed code examples (mirrors the public API docs page)
const CodeTabs: React.FC<{
  tabs: { label: string; code: string; language: string }[];
}> = ({ tabs }) => {
  const [activeTab, setActiveTab] = useState(0);

  return (
    <div className="api-docs-tabs">
      <div className="api-docs-tab-list">
        {tabs.map((tab, index) => (
          <button
            key={tab.label}
            className={`api-docs-tab ${index === activeTab ? 'active' : ''}`}
            onClick={() => setActiveTab(index)}
          >
            {tab.label}
          </button>
        ))}
      </div>
      {tabs.map((tab, index) => (
        <div
          key={tab.label}
          className={`api-docs-tab-content ${index === activeTab ? 'active' : ''}`}
        >
          <CodeBlock code={tab.code} label={tab.language} />
        </div>
      ))}
    </div>
  );
};

const ENDPOINT_URL = 'https://api.trademagic.gg/api/v1/partner/wanted/lookup';

const PartnerApiPage: React.FC = () => {
  const currentUser = useSelector((state: RootState) => state.auth.user);

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  // The plaintext key is returned only by regenerate and shown once. It is held
  // in component state and never persisted (the backend stores only a hash).
  const [plaintextKey, setPlaintextKey] = useState<string | null>(null);
  const [keyCopied, setKeyCopied] = useState(false);

  const { data: keyMeta, isLoading: isLoadingKey } = useGetMyApiKeyQuery();
  const [regenerateKey, { isLoading: isRegenerating }] =
    useRegenerateApiKeyMutation();
  const [revokeKey, { isLoading: isRevoking }] = useRevokeApiKeyMutation();

  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth <= 768);
      if (window.innerWidth > 768) {
        setIsMobileMenuOpen(false);
      }
    };

    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  // A freshly-flagged partner has hasKey: true (admin provisioned one) but the
  // plaintext was discarded, so they have nothing usable until they generate.
  // Treat that, and the no-key case, as a first-run "generate to get started"
  // state rather than a dead "you have a key" panel. (activated === false is the
  // backend signal for the discarded-plaintext case; when the backend omits the
  // field we fall back to treating an existing key as active.)
  const needsFirstGeneration =
    !keyMeta?.hasKey || keyMeta?.activated === false;

  const handleRegenerate = async () => {
    const confirmMessage = needsFirstGeneration
      ? 'Generate your API key?'
      : 'Regenerate your API key? The current key will stop working right away and any integration using it must be updated.';
    if (!window.confirm(confirmMessage)) return;

    try {
      const result = await regenerateKey().unwrap();
      setPlaintextKey(result.key);
      setKeyCopied(false);
      toast.success('New API key generated. Copy it now, it will not be shown again.');
    } catch (error: any) {
      toast.error(error?.data?.message || 'Failed to generate a new API key');
    }
  };

  const handleRevoke = async () => {
    if (
      !window.confirm(
        'Revoke your API key? Live lookup requests will stop working until you generate a new key.'
      )
    ) {
      return;
    }
    try {
      await revokeKey().unwrap();
      setPlaintextKey(null);
      toast.success('API key revoked');
    } catch (error: any) {
      toast.error(error?.data?.message || 'Failed to revoke API key');
    }
  };

  const handleCopyKey = async () => {
    if (!plaintextKey) return;
    if (!(await copyToClipboard(plaintextKey))) return;
    setKeyCopied(true);
    setTimeout(() => setKeyCopied(false), 2000);
  };

  const formatDate = (value: string | null | undefined) => {
    if (!value) return 'Never';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return 'Unknown';
    return date.toLocaleString();
  };

  const codeExamples = [
    {
      label: 'cURL',
      language: 'Shell',
      code: `curl -X POST ${ENDPOINT_URL} \\
  -H "X-API-Key: YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{ "scryfallIds": ["3efc0622-ac2c-4722-ba05-961cc98c5940"] }'`,
    },
    {
      label: 'JavaScript',
      language: 'JavaScript',
      code: `const response = await fetch(
  '${ENDPOINT_URL}',
  {
    method: 'POST',
    headers: {
      'X-API-Key': 'YOUR_API_KEY',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      scryfallIds: ['3efc0622-ac2c-4722-ba05-961cc98c5940'],
    }),
  }
);

const data = await response.json();
console.log(data.results);`,
    },
    {
      label: 'Python',
      language: 'Python',
      code: `import requests

response = requests.post(
    "${ENDPOINT_URL}",
    headers={"X-API-Key": "YOUR_API_KEY"},
    json={"scryfallIds": ["3efc0622-ac2c-4722-ba05-961cc98c5940"]},
)

data = response.json()
print(data["results"])`,
    },
  ];

  return (
    <>
      <Helmet>
        <title>Partner API - TradeMagic</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="api-docs-layout">
        {isMobile && isMobileMenuOpen && (
          <div
            className="api-docs-mobile-overlay"
            onClick={() => setIsMobileMenuOpen(false)}
            aria-hidden="true"
          />
        )}

        <div className="api-docs-content-wrapper">
          <LeftNavigationBar
            currentTab={{ path: '/partner-api', Component: <></> }}
            isAuthenticated={!!currentUser}
            isMobile={isMobile}
            isMobileMenuOpen={isMobileMenuOpen}
            onMenuClose={() => setIsMobileMenuOpen(false)}
          />

          <div className="api-docs-main">
            <TopNavigationBar
              title="Partner API"
              isMobileMenuOpen={isMobileMenuOpen}
              onToggleMobileMenu={toggleMobileMenu}
            />

            <div className="api-docs-container">
              {/* Header */}
              <header className="api-docs-header">
                <h1 className="api-docs-title">Partner API</h1>
                <p className="api-docs-subtitle">
                  Live, per-card trade demand for approved partners
                </p>
              </header>

              {/* API Key panel */}
              <section className="api-docs-section">
                <h2 className="api-docs-section-title">Your API Key</h2>
                <p className="api-docs-text">
                  Your API key authenticates every live lookup request. Treat it
                  like a password. Send it on the{' '}
                  <code className="partner-api-inline-code">X-API-Key</code>{' '}
                  header. If your key is ever exposed, regenerate it here to
                  invalidate the old one.
                </p>

                <div className="partner-api-key-panel">
                  {isLoadingKey ? (
                    <p className="api-docs-text">Loading key status...</p>
                  ) : plaintextKey ? (
                    <>
                      <div className="partner-api-key-warning">
                        Copy this key now. For your security it will not be shown
                        again. If you lose it, generate a new one.
                      </div>
                      <div className="partner-api-key-value-row">
                        <code className="partner-api-key-value">
                          {plaintextKey}
                        </code>
                        <button
                          className={`api-docs-copy-btn ${keyCopied ? 'copied' : ''}`}
                          onClick={handleCopyKey}
                        >
                          {keyCopied ? 'Copied!' : 'Copy'}
                        </button>
                      </div>
                    </>
                  ) : needsFirstGeneration ? (
                    <p className="api-docs-text">
                      Generate your API key to get started. The full key is shown
                      once, at the moment you generate it, so copy it somewhere
                      safe right away.
                    </p>
                  ) : (
                    <div className="partner-api-key-meta">
                      <div className="partner-api-key-meta-row">
                        <span className="partner-api-key-meta-label">Key</span>
                        <span className="partner-api-key-meta-value">
                          {keyMeta?.keyPrefix
                            ? `${keyMeta.keyPrefix}${'•'.repeat(24)}`
                            : 'Active'}
                        </span>
                      </div>
                      <div className="partner-api-key-meta-row">
                        <span className="partner-api-key-meta-label">
                          Created
                        </span>
                        <span className="partner-api-key-meta-value">
                          {formatDate(keyMeta?.createdAt)}
                        </span>
                      </div>
                      <div className="partner-api-key-meta-row">
                        <span className="partner-api-key-meta-label">
                          Last used
                        </span>
                        <span className="partner-api-key-meta-value">
                          {formatDate(keyMeta?.lastUsedAt)}
                        </span>
                      </div>
                      <p className="api-docs-text partner-api-key-note">
                        The full key is shown only when you generate it. If you
                        do not have it saved, regenerate to get a new one.
                      </p>
                    </div>
                  )}

                  <div className="partner-api-key-actions">
                    <button
                      className="partner-api-btn partner-api-btn-primary"
                      onClick={handleRegenerate}
                      disabled={isRegenerating || isRevoking}
                    >
                      {isRegenerating
                        ? 'Generating...'
                        : needsFirstGeneration
                          ? 'Generate Key'
                          : 'Regenerate Key'}
                    </button>
                    {!needsFirstGeneration && (
                      <button
                        className="partner-api-btn partner-api-btn-danger"
                        onClick={handleRevoke}
                        disabled={isRevoking || isRegenerating}
                      >
                        {isRevoking ? 'Revoking...' : 'Revoke Key'}
                      </button>
                    )}
                  </div>
                </div>
              </section>

              {/* Overview */}
              <section className="api-docs-section">
                <h2 className="api-docs-section-title">Overview</h2>
                <p className="api-docs-text">
                  The live lookup tells you whether specific cards are wanted on
                  TradeMagic right now. It is built for per-card features like a
                  binder that flags which cards a user can trade. Send a batch of
                  Scryfall IDs and get back current demand counts for each one,
                  computed against live data at request time.
                </p>
                <p className="api-docs-text">
                  The lookup reports demand for the specific printing you pass,
                  not for the card in general. A Scryfall ID is counted as wanted
                  only when that exact printing falls inside an active want that
                  accepts it, using the same fulfillment rule as auto-match and
                  Send Cards.
                </p>
              </section>

              {/* Matching */}
              <section className="api-docs-section">
                <h2 className="api-docs-section-title">How matching works</h2>
                <p className="api-docs-text">
                  Every TradeMagic want names a card and carries the set of
                  printings it will accept. The lookup counts a wanter for your
                  Scryfall ID only when that printing is in the want accepted set.
                </p>
                <p className="api-docs-text">
                  A Scryfall ID fixes the printing and the language, so the
                  lookup matches on those two dimensions. A want restricted to
                  other printings, or to other languages, does not count. Finish
                  and condition are not carried by a bare Scryfall ID, so the
                  lookup does not filter on them. Finish is still partly reflected
                  because a foil-only want already accepts only foil-capable
                  printings.
                </p>
                <p className="api-docs-text">
                  This is stricter than the bulk wanted export, which aggregates
                  demand at the oracle level across every printing of a card. The
                  two endpoints answer different questions, so their numbers are
                  expected to differ: for a given printing the lookup count will
                  be the same or lower than the bulk number.
                </p>
              </section>

              {/* Endpoint */}
              <section className="api-docs-section">
                <h2 className="api-docs-section-title">Endpoint</h2>
                <div className="api-docs-endpoint">
                  <span className="api-docs-method-badge">POST</span>
                  <span className="api-docs-endpoint-url">{ENDPOINT_URL}</span>
                  <p className="api-docs-endpoint-note">
                    Content-Type: application/json | Auth: X-API-Key header
                  </p>
                </div>
              </section>

              {/* Authentication */}
              <section className="api-docs-section">
                <h2 className="api-docs-section-title">Authentication</h2>
                <p className="api-docs-text">
                  Pass your key on the{' '}
                  <code className="partner-api-inline-code">X-API-Key</code>{' '}
                  header on every request. A missing, invalid, or revoked key
                  returns 401.
                </p>
                <CodeBlock label="HTTP" code={`X-API-Key: YOUR_API_KEY`} />
              </section>

              {/* Request */}
              <section className="api-docs-section">
                <h2 className="api-docs-section-title">Request</h2>
                <p className="api-docs-text">
                  Send a JSON body with an array of Scryfall printing IDs. A
                  single card is just a one element array. The maximum batch size
                  is 100 IDs per request.
                </p>
                <CodeBlock
                  label="JSON"
                  code={`{
  "scryfallIds": [
    "3efc0622-ac2c-4722-ba05-961cc98c5940",
    "a1b2c3d4-0000-0000-0000-000000000000"
  ]
}`}
                />
                <div className="api-docs-table-wrapper">
                  <table className="api-docs-table">
                    <thead>
                      <tr>
                        <th>Field</th>
                        <th>Type</th>
                        <th>Required</th>
                        <th>Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>scryfallIds</td>
                        <td>string[]</td>
                        <td>Yes</td>
                        <td>
                          Scryfall printing IDs to look up. 1 to 100 per
                          request.
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </section>

              {/* Response */}
              <section className="api-docs-section">
                <h2 className="api-docs-section-title">Response</h2>
                <p className="api-docs-text">
                  You get back one result per requested ID, in the same order.
                  Each result echoes the ID you sent.
                </p>
                <CodeBlock
                  label="JSON"
                  code={`{
  "generatedAt": "2026-06-23T18:00:00.000Z",
  "results": [
    {
      "scryfallId": "3efc0622-ac2c-4722-ba05-961cc98c5940",
      "found": true,
      "wanterCount": 7,
      "affordableWanterCount": 3
    },
    {
      "scryfallId": "a1b2c3d4-0000-0000-0000-000000000000",
      "found": false,
      "wanterCount": 0,
      "affordableWanterCount": 0
    }
  ]
}`}
                />
                <div className="api-docs-table-wrapper">
                  <table className="api-docs-table">
                    <thead>
                      <tr>
                        <th>Field</th>
                        <th>Type</th>
                        <th>Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>scryfallId</td>
                        <td>string</td>
                        <td>The requested ID, echoed back.</td>
                      </tr>
                      <tr>
                        <td>found</td>
                        <td>boolean</td>
                        <td>
                          True when the ID resolves to a known card. False marks
                          an unknown ID without failing the rest of the batch.
                        </td>
                      </tr>
                      <tr>
                        <td>wanterCount</td>
                        <td>number</td>
                        <td>
                          Active traders who would accept this specific printing
                          right now. A known printing that no active want accepts
                          returns 0, which is different from an unknown card.
                        </td>
                      </tr>
                      <tr>
                        <td>affordableWanterCount</td>
                        <td>number</td>
                        <td>
                          The subset of those wanters whose balance covers the
                          card reference value.
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <p className="api-docs-text">
                  A known printing that no active want accepts returns{' '}
                  <code className="partner-api-inline-code">found: true</code>{' '}
                  with{' '}
                  <code className="partner-api-inline-code">wanterCount: 0</code>,
                  meaning the card exists but is not currently wanted in this
                  printing. An ID that does not resolve to any card returns{' '}
                  <code className="partner-api-inline-code">found: false</code>.
                  The response contains only card identity and the two counts. It
                  never includes usernames, balances, or per-user wants.
                </p>
              </section>

              {/* Examples */}
              <section className="api-docs-section">
                <h2 className="api-docs-section-title">Code Examples</h2>
                <CodeTabs tabs={codeExamples} />
              </section>

              {/* Limits */}
              <section className="api-docs-section">
                <h2 className="api-docs-section-title">Limits and Caching</h2>
                <div className="api-docs-table-wrapper">
                  <table className="api-docs-table">
                    <thead>
                      <tr>
                        <th>Limit</th>
                        <th>Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>Batch size</td>
                        <td>100 IDs per request</td>
                      </tr>
                      <tr>
                        <td>Rate limit</td>
                        <td>120 requests per minute per key</td>
                      </tr>
                      <tr>
                        <td>Response cache</td>
                        <td>About 60 seconds per identical batch</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <p className="api-docs-text">
                  Counts reflect live data with up to about a minute of caching,
                  which keeps a binder that scrolls many cards from overloading
                  the service. A batch over 100 IDs returns 400. Going over the
                  rate limit returns 429.
                </p>
              </section>

              {/* More resources */}
              <section className="api-docs-section">
                <h2 className="api-docs-section-title">More resources</h2>
                <p className="api-docs-text">
                  <Link to="/docs/api" className="api-docs-inline-link">
                    Public API documentation
                  </Link>{' '}
                  covers the import API and the public bulk price and wanted
                  exports, which give a broad, snapshot-based view of demand.
                </p>
                <p className="api-docs-text">
                  <Link to="/brand" className="api-docs-inline-link">
                    Brand and style guide
                  </Link>{' '}
                  has our logos, colors, and usage rules for building partner
                  experiences that match the TradeMagic look.
                </p>
              </section>

              {/* Support */}
              <div className="api-docs-support">
                <h2 className="api-docs-support-title">Need Help?</h2>
                <p className="api-docs-support-text">
                  Questions about the partner integration?
                </p>
                <a
                  href="mailto:support@trademagic.gg"
                  className="api-docs-support-email"
                >
                  support@trademagic.gg
                </a>
              </div>
            </div>
          </div>
        </div>

        <Footer />
      </div>
    </>
  );
};

export default PartnerApiPage;
